import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, Card, Button, Badge, ProgressBar, Tabs, TabPanel } from '../ui';
import WorkerStatusBanner from './WorkerStatusBanner';
import useJobProgress, { isTerminal } from './useJobProgress';
import { createJob, listDiffs, cancelJob } from '../../services/syncApi';
import { createEpicorJob, cancelEpicorJob } from '../../services/epicorApi';
import { listFamilies } from '../../services/pimApi';
import { getAuditSummary, searchPricingCustomers } from '../../services/pricingApi';
import { TypeAhead } from './PriceLookup';
import { SYNC_TARGETS, DEFAULT_SYNC_TARGET } from '../../constants/markets';

const ALL_PRODUCT_STAGES = ['create', 'update', 'assign_channel', 'assign_category', 'images'];

// Guarded localStorage helper so a tracked job survives navigating away and
// back (and a page reload). All access is wrapped so a disabled/throwing
// localStorage never breaks the page. Entries are namespaced per card key.
const STORE_PREFIX = 'waterco.manualSync.';
const jobStore = {
  get(key) {
    try {
      const raw = window.localStorage.getItem(STORE_PREFIX + key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && parsed.jobId ? parsed : null;
    } catch (e) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(STORE_PREFIX + key, JSON.stringify(value));
    } catch (e) {
      /* ignore */
    }
  },
  clear(key) {
    try {
      window.localStorage.removeItem(STORE_PREFIX + key);
    } catch (e) {
      /* ignore */
    }
  },
};

// Standalone store for the page's UI selections (target, tab, dry-run). Kept
// separate from jobStore because jobStore.get gates on a `.jobId` field and
// would never read these plain values back. JSON-encodes so booleans (and any
// future non-string values) round-trip instead of localStorage coercing them
// to strings (where `'false'` is truthy). Returns the supplied fallback when
// missing, invalid, or storage is unavailable.
const uiStore = {
  get(key, fallback) {
    try {
      const raw = window.localStorage.getItem(STORE_PREFIX + key);
      if (raw == null) return fallback;
      return JSON.parse(raw);
    } catch (e) {
      return fallback;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(STORE_PREFIX + key, JSON.stringify(value));
    } catch (e) {
      /* ignore */
    }
  },
};

// Product push actions (BigCommerce sync queue).
const PRODUCT_ACTIONS = [
  {
    id: 'product_full',
    kind: 'product',
    title: 'Product Full Sync',
    desc: 'Push every eligible product to BigCommerce: create, update, then channel and category assignment.',
  },
  {
    id: 'product_changes',
    kind: 'product',
    title: 'Product Changes Sync',
    desc: 'Compare against BigCommerce first, then push only the SKUs that are out of sync.',
  },
];

// Inbound product sync actions: Epicor -> PIM (product_details/product_channel_locales).
// These run on the Epicor queue and write to Postgres only (no BigCommerce mutation).
const EPICOR_PRODUCT_ACTIONS = [
  {
    id: 'epicor_product_full',
    kind: 'epicor_product',
    title: 'Product Full Sync (Epicor \u2192 PIM)',
    desc: 'Pull every part from Epicor (AU + NZ), upsert core ERP fields into staging, then backfill the PIM catalog (classification, brand, product group/type, web flags, market presence, list price). Marketing copy (product_name, description) stays PIM-owned until enriched.',
  },
  {
    id: 'epicor_product_changes',
    kind: 'epicor_product',
    title: 'Product Changes Sync (Epicor \u2192 PIM)',
    desc: 'Pull only parts changed since the last run (Epicor SysRevID watermark), update staging, and backfill the PIM catalog. Additive; run a Full Sync periodically to reconcile removals.',
  },
];

// Inventory pull actions: Epicor PartWhse -> inventory levels/history. These run
// on the Epicor queue and write to Postgres only — inventory is NEVER pushed to
// BigCommerce (the B2B site reads the middleware availability API instead).
const EPICOR_INVENTORY_ACTIONS = [
  {
    id: 'epicor_inventory_full',
    kind: 'epicor_inventory',
    title: 'Inventory Full Sync',
    desc: 'Pull every per-SKU, per-warehouse stock row from Epicor (AU + NZ), record availability buckets (on hand, reserved, allocated, picking, picked), and reconcile removals. Also refreshes the warehouse master and prunes expired history.',
  },
  {
    id: 'epicor_inventory_changes',
    kind: 'epicor_inventory',
    title: 'Inventory Changes Sync',
    desc: 'Pull only stock rows changed since the last sync (Epicor SysRevID watermark). Cheap enough to run every few minutes; run a Full Sync periodically to reconcile removals.',
  },
];

// Warehouse master refresh (Erp.Warehse -> epicor_warehouses). Also runs
// implicitly at the start of every inventory sync; this card is the cheap
// standalone trigger.
const EPICOR_WAREHOUSE_ACTIONS = [
  {
    id: 'epicor_warehouse_sync',
    kind: 'epicor_warehouse',
    title: 'Warehouse Sync',
    desc: 'Refresh the warehouse master list (codes, descriptions, plants, Epicor active/inactive status) from Epicor for AU + NZ. Manual in-use toggles are never touched. Stock levels are not pulled — use the Inventory syncs for that.',
  },
];

// Credit availability recompute (-> epicor_customer_credit_snapshot). Normally
// chained off the customers sync, so this card is only needed to rebuild the
// snapshot out of band. Writes to Postgres only; the B2B site reads it through
// the middleware credit API.
const EPICOR_CREDIT_ACTIONS = [
  {
    id: 'epicor_credit_snapshot',
    kind: 'epicor_credit',
    title: 'Credit Snapshot Rebuild',
    desc: 'Recompute available credit for every BigCommerce-eligible customer (AU + NZ): credit limit less open invoices and, where Epicor\u2019s CredInc flags allow, open orders. Open balances are read live from Epicor, so the figures are current regardless of when the last Sales Orders sync ran. Takes about a second.',
  },
];

// Customer (company) push actions (Epicor queue -> BigCommerce B2B companies).
const CUSTOMER_PUSH_ACTIONS = [
  {
    id: 'customer_full',
    kind: 'customer',
    title: 'Company Full Sync',
    desc: 'Create or update a B2B Edition Company for every open Epicor customer and backfill the BigCommerce company id. Closed customers (inactive/absent) are deactivated if already synced, or skipped if never synced. (Addresses are handled by the Ship-to syncs below.)',
  },
  {
    id: 'customer_changes',
    kind: 'customer',
    title: 'Company Changes Sync',
    desc: 'Push only the customers changed since the last push (Epicor SysRevID watermark): create/update open companies, deactivate ones that have closed.',
  },
];

// Ship-to / address actions in both directions.
const SHIPTO_ACTIONS = [
  {
    id: 'shipto_pull',
    kind: 'shipto_pull',
    title: 'Ship-to Pull (Epicor \u2192 staging)',
    desc: 'Pull every ship-to address from Epicor into staging for the selected company. Read-only towards Epicor; nothing is pushed to BigCommerce.',
  },
  {
    id: 'address_push',
    kind: 'address_push',
    title: 'Ship-to Push (\u2192 BigCommerce)',
    desc: 'Push staged company / ship-to addresses to BigCommerce as B2B Company addresses. Only customers already synced as a B2B Company receive addresses.',
  },
];

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : n ?? '0');
const num = (v) => Number(v) || 0;

// Determinate progress for product jobs. Compare uses compared/total; mutate
// uses equal-weight across the planned stages (matches JobDetail's heuristic).
const productProgress = ({ mode, plannedStages, counters, stagesCompleted, status }) => {
  const c = counters || {};
  if (mode === 'compare') {
    const total = Number(c.total) || 0;
    const compared = Number(c.compared) || 0;
    if (total > 0) {
      const pct = Math.min(100, Math.round((compared / total) * 100));
      return { pct, indeterminate: false, label: `Comparing ${fmt(compared)} / ${fmt(total)} (${pct}%)` };
    }
    return { pct: 0, indeterminate: status === 'running' || status === 'pending', label: 'Comparing…' };
  }
  const planned = plannedStages && plannedStages.length ? plannedStages : ALL_PRODUCT_STAGES;
  const completed = (stagesCompleted || []).filter((s) => planned.includes(s)).length;
  const curTotal = Number(c.current_stage_total) || 0;
  const curDoneRaw = Number(c.current_stage_done) || 0;
  const curDone = curTotal > 0 ? Math.min(curDoneRaw, curTotal) : curDoneRaw;
  const frac = curTotal > 0 ? curDone / curTotal : 0;
  const pct = Math.min(100, Math.round(((completed + frac) / planned.length) * 100));
  const stageName = c.current_stage || planned[Math.min(completed, planned.length - 1)];
  return { pct, indeterminate: false, label: `${stageName ? `${stageName}: ` : ''}${pct}%` };
};

// Determinate percent for push jobs (eligible/processed known up front once the
// candidate set is loaded). Capped because processed can exceed eligible on
// retries. Returns null when no total is available yet.
const pushPct = (c) => {
  const eligible = num(c.eligible);
  return eligible > 0 ? Math.min(100, Math.round((num(c.processed) / eligible) * 100)) : null;
};

// Company push jobs report eligible/processed once the candidate set is loaded,
// so the bar fills as a real percent; we also surface the company counters as
// text. Falls back to indeterminate before a total is known.
const customerProgress = ({ counters, status }) => {
  const c = counters || {};
  const running = status === 'running' || status === 'pending';
  const segs = [`${fmt(num(c.companies_created))} created`, `${fmt(num(c.companies_updated))} updated`];
  if (num(c.companies_unchanged)) segs.push(`${fmt(num(c.companies_unchanged))} unchanged`);
  if (num(c.companies_deactivated)) segs.push(`${fmt(num(c.companies_deactivated))} deactivated`);
  if (num(c.skipped_closed)) segs.push(`${fmt(num(c.skipped_closed))} skipped`);
  const errors = num(c.errors_create) + num(c.errors_update) + num(c.errors_status);
  if (errors) segs.push(`${fmt(errors)} errors`);
  const pct = pushPct(c);
  if (pct != null) {
    return {
      pct,
      indeterminate: false,
      label: `${segs.join(' · ')} · ${fmt(num(c.processed))}/${fmt(num(c.eligible))} (${pct}%)`,
    };
  }
  return { pct: 0, indeterminate: running, label: segs.join(' · ') };
};

// Price list push jobs (BC sync queue, price_lists stage, per-customer scope).
// Counters flush per customer, so the bar advances as customers complete.
const pricingProgress = ({ counters, status }) => {
  const c = counters || {};
  const running = status === 'running' || status === 'pending';
  const segs = [];
  if (num(c.bc_price_lists_created)) segs.push(`${fmt(num(c.bc_price_lists_created))} lists created`);
  if (num(c.bc_groups_created)) segs.push(`${fmt(num(c.bc_groups_created))} groups created`);
  segs.push(`${fmt(num(c.price_records_upserted))} records upserted`);
  if (num(c.price_records_deleted)) segs.push(`${fmt(num(c.price_records_deleted))} deleted`);
  if (num(c.price_records_in_sync)) segs.push(`${fmt(num(c.price_records_in_sync))} in sync`);
  if (num(c.customers_group_assigned)) segs.push(`${fmt(num(c.customers_group_assigned))} customers assigned`);
  if (num(c.customer_errors)) segs.push(`${fmt(num(c.customer_errors))} errors`);
  if (num(c.customers_excluded_inactive)) segs.push(`${fmt(num(c.customers_excluded_inactive))} skipped (inactive)`);
  const total = num(c.current_stage_total) || num(c.customers_considered);
  const done = num(c.customers_processed) || num(c.current_stage_done);
  if (total > 0) {
    const pct = Math.min(100, Math.round((done / total) * 100));
    return {
      pct,
      indeterminate: false,
      label: `${segs.join(' · ')} · ${fmt(done)}/${fmt(total)} customers (${pct}%)`,
    };
  }
  return { pct: 0, indeterminate: running, label: segs.join(' · ') };
};

// Generic progress for the other Epicor-queue jobs. The address push reports
// eligible/processed (determinate percent); pulls have no upfront total and
// stay indeterminate while running.
const epicorProgress = ({ kind, counters, status }) => {
  if (kind === 'customer') return customerProgress({ counters, status });
  const c = counters || {};
  const running = status === 'running' || status === 'pending';
  let segs = [];
  if (kind === 'shipto_pull') {
    segs = [`${fmt(num(c.fetched))} fetched`, `${fmt(num(c.upserted))} upserted`];
    if (num(c.marked_absent)) segs.push(`${fmt(num(c.marked_absent))} absent`);
  } else if (kind === 'address_push') {
    segs = [
      `${fmt(num(c.addresses_created))} created`,
      `${fmt(num(c.addresses_updated))} updated`,
      `${fmt(num(c.addresses_unchanged))} unchanged`,
    ];
    if (num(c.addresses_skipped)) segs.push(`${fmt(num(c.addresses_skipped))} skipped`);
    if (num(c.state_dropped)) segs.push(`${fmt(num(c.state_dropped))} state-dropped`);
    if (num(c.errors_address)) segs.push(`${fmt(num(c.errors_address))} errors`);
    const pct = pushPct(c);
    if (pct != null) {
      return {
        pct,
        indeterminate: false,
        label: `${segs.join(' · ')} · ${fmt(num(c.processed))}/${fmt(num(c.eligible))} (${pct}%)`,
      };
    }
  } else if (kind === 'customer_pull') {
    segs = [`${fmt(num(c.customers_upserted))} customers`, `${fmt(num(c.shiptos_upserted))} ship-tos`];
  } else if (kind === 'epicor_inventory') {
    segs = [`${fmt(num(c.fetched))} fetched`, `${fmt(num(c.updated))} changed`];
    if (num(c.unchanged)) segs.push(`${fmt(num(c.unchanged))} unchanged`);
    if (num(c.history_rows)) segs.push(`${fmt(num(c.history_rows))} history rows`);
    if (num(c.marked_absent)) segs.push(`${fmt(num(c.marked_absent))} absent`);
    if (num(c.warehouses)) segs.push(`${fmt(num(c.warehouses))} warehouses`);
  } else if (kind === 'epicor_warehouse') {
    segs = [`${fmt(num(c.warehouses))} warehouses`];
  } else if (kind === 'epicor_product') {
    segs = [`${fmt(num(c.fetched))} fetched`, `${fmt(num(c.upserted))} products`];
    if (num(c.pcl_upserted)) segs.push(`${fmt(num(c.pcl_upserted))} channel rows`);
    if (num(c.marked_absent)) segs.push(`${fmt(num(c.marked_absent))} absent`);
    // PIM chain counters (single-SKU "to PIM" runs).
    if (num(c.pim_products)) segs.push(`${fmt(num(c.pim_products))} to PIM`);
    if (num(c.pim_global_values)) segs.push(`${fmt(num(c.pim_global_values))} values`);
    if (num(c.projection_refreshed)) segs.push(`${fmt(num(c.projection_refreshed))} projected`);
  }
  return { pct: 0, indeterminate: running, label: segs.join(' · ') };
};

// Paginate the diff list and collect every SKU that isn't in_sync.
const collectChangedSkus = async (market, environment) => {
  const limit = 500;
  const skus = new Set();
  let offset = 0;
  for (let i = 0; i < 60; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const res = await listDiffs({ market_code: market, environment, limit, offset });
    const rows = res?.data || [];
    rows.forEach((r) => {
      if (r.status && r.status !== 'in_sync') skus.add(r.sku);
    });
    if (rows.length < limit) break;
    offset += limit;
  }
  return Array.from(skus);
};

// A mutate job that "succeeded" without matching any publishable rows is a
// no-op, not a real push (the SKUs were gated out by publish/present/active).
// Surface it as a warning instead of a green success.
const isNoOpMutate = (progress) =>
  progress.status === 'succeeded' && progress.counters?.no_desired_rows === true;

// Shared progress-bar renderer for a tracked job.
const JobProgressBar = ({ kind, progress, phase }) => {
  const isProduct = kind === 'product';
  const term = isTerminal(progress.status);
  if (!(progress.id || phase === 'comparing' || phase === 'listing' || phase === 'applying')) {
    return null;
  }
  const base = isProduct
    ? productProgress({
        mode: phase === 'comparing' || phase === 'listing' ? 'compare' : 'mutate',
        plannedStages: ALL_PRODUCT_STAGES,
        counters: progress.counters,
        stagesCompleted: progress.stagesCompleted,
        status: progress.status,
      })
    : kind === 'pricing'
      ? pricingProgress({ counters: progress.counters, status: progress.status })
      : epicorProgress({ kind, counters: progress.counters, status: progress.status });
  let { pct, indeterminate, label } = base;
  let tone;
  if (phase === 'listing') {
    indeterminate = true;
    label = 'Collecting changed SKUs…';
  } else if (term) {
    indeterminate = false;
    if (isNoOpMutate(progress)) {
      pct = 100;
      tone = 'warning';
      label = 'Nothing pushed';
    } else if (progress.status === 'succeeded') {
      pct = 100;
      tone = 'success';
      label = isProduct ? 'Complete' : base.label;
    } else {
      tone = 'danger';
      label = `Job ${progress.status}`;
    }
  }
  return <ProgressBar pct={pct} indeterminate={indeterminate} label={label} tone={tone} />;
};

// Link to the right job history view for the queue this job lives in.
const JobLinks = ({ kind, progress, jobLinks }) => {
  if (jobLinks.length === 0) return null;
  const term = isTerminal(progress.status);
  // Pricing jobs run on the same BC sync queue as product jobs.
  const isProduct = kind === 'product' || kind === 'pricing';
  const noOp = isNoOpMutate(progress);
  return (
    <p className="manual-sync-links">
      {progress.status && (
        <Badge
          variant={
            term
              ? noOp
                ? 'warning'
                : progress.status === 'succeeded'
                  ? 'success'
                  : 'danger'
              : 'info'
          }
        >
          {noOp ? 'Nothing pushed' : progress.status}
        </Badge>
      )}{' '}
      {jobLinks.map((j, i) => (
        <React.Fragment key={j.id}>
          {i > 0 && ' · '}
          <Link to={isProduct ? `/jobs/history/${j.id}` : '/jobs/history?queue=epicor'}>{j.label}</Link>
        </React.Fragment>
      ))}
    </p>
  );
};

// Warning banner shown when a push completed as a no-op (see isNoOpMutate).
// The last job link is the mutate job, so point the detail link at it.
const NoOpPushBanner = ({ progress, jobLinks }) => {
  if (!isNoOpMutate(progress)) return null;
  const lastJob = jobLinks[jobLinks.length - 1];
  return (
    <div className="banner warning" role="alert">
      Nothing matched: no publishable products for this store, so nothing was created or updated.
      The SKU(s) are likely unpublished, incomplete, or not present — publish them in the PIM
      first.
      {lastJob && (
        <>
          {' '}
          <Link to={`/jobs/history/${lastJob.id}`}>View job #{lastJob.id}</Link>
        </>
      )}
    </div>
  );
};

const ActionCard = ({ action, target, dryRun }) => {
  const progress = useJobProgress();
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | comparing | listing | applying | error | done
  const [phaseNote, setPhaseNote] = useState(null);
  const [error, setError] = useState(null);
  const [jobLinks, setJobLinks] = useState([]); // [{ id, label }]
  const [cancelling, setCancelling] = useState(false);

  const { market, environment } = target;
  const isCustomer = action.kind === 'customer';
  const running = busy || ['pending', 'running'].includes(progress.status);

  // Stable per (action, target) for this mount: the parent remounts each card
  // per target via key={...:target.id}.
  const storageKey = `${action.id}:${target.id}`;

  // Resume on mount: re-attach to a job that was running when we left.
  useEffect(() => {
    const saved = jobStore.get(storageKey);
    if (saved && saved.jobId) {
      setJobLinks([{ id: saved.jobId, label: `#${saved.jobId}` }]);
      setPhase('done');
      progress.track(saved.jobId, saved.kind);
    }
    // Mount-once on the stable storageKey; progress.track/setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Persist while live; clear once terminal. Decoupled from the run call sites.
  useEffect(() => {
    if (!progress.id) return;
    if (isTerminal(progress.status)) {
      jobStore.clear(storageKey);
      setCancelling(false);
    } else {
      jobStore.set(storageKey, { jobId: progress.id, kind: progress.kind });
    }
  }, [progress.id, progress.status, progress.kind, storageKey]);

  const onCancel = useCallback(async () => {
    if (!progress.id) return;
    setCancelling(true);
    try {
      if (action.kind === 'product') await cancelJob(progress.id);
      else await cancelEpicorJob(progress.id);
    } catch (e) {
      setCancelling(false);
      setError(e?.response?.data?.detail || e.message || 'Failed to cancel');
    }
  }, [action.kind, progress.id]);

  const fail = useCallback((msg) => {
    setError(msg);
    setPhase('error');
    setBusy(false);
  }, []);

  const runProductFull = useCallback(async () => {
    setBusy(true);
    try {
      const { job_id } = await createJob({
        store_code: market,
        environment,
        mode: 'mutate',
        stage_filter: ALL_PRODUCT_STAGES,
        skus: null,
        dry_run: dryRun,
      });
      setJobLinks([{ id: job_id, label: `#${job_id}` }]);
      setPhase('done');
      progress.track(job_id, 'product', { onClose: () => setBusy(false) });
      setBusy(false);
    } catch (e) {
      fail(e?.response?.data?.detail || e.message || 'Failed to enqueue');
    }
  }, [market, environment, dryRun, progress, fail]);

  const runProductChanges = useCallback(async () => {
    setBusy(true);
    setPhase('comparing');
    setPhaseNote(null);
    try {
      const { job_id: compareId } = await createJob({
        store_code: market,
        environment,
        mode: 'compare',
      });
      setJobLinks([{ id: compareId, label: `compare #${compareId}` }]);
      progress.track(compareId, 'product', {
        onTerminal: async (payload) => {
          if (payload.status !== 'succeeded') {
            fail(`Compare ${payload.status}. See job #${compareId}.`);
            return;
          }
          setPhase('listing');
          try {
            const skus = await collectChangedSkus(market, environment);
            if (skus.length === 0) {
              setPhaseNote('Everything is already in sync — nothing to apply.');
              setPhase('done');
              setBusy(false);
              return;
            }
            setPhaseNote(`${fmt(skus.length)} SKU${skus.length === 1 ? '' : 's'} out of sync — applying changes.`);
            setPhase('applying');
            const { job_id: mutateId } = await createJob({
              store_code: market,
              environment,
              mode: 'mutate',
              stage_filter: ALL_PRODUCT_STAGES,
              skus,
              dry_run: dryRun,
            });
            setJobLinks((prev) => [...prev, { id: mutateId, label: `mutate #${mutateId}` }]);
            progress.track(mutateId, 'product', { onClose: () => setBusy(false) });
            setPhase('done');
            setBusy(false);
          } catch (e) {
            fail(e?.response?.data?.detail || e.message || 'Failed to apply changes');
          }
        },
      });
    } catch (e) {
      fail(e?.response?.data?.detail || e.message || 'Failed to enqueue compare');
    }
  }, [market, environment, dryRun, progress, fail]);

  // Generic Epicor-queue action: customer push, ship-to pull, address push.
  const runEpicor = useCallback(
    async (jobPayload) => {
      setBusy(true);
      try {
        const { job_id } = await createEpicorJob(jobPayload);
        setJobLinks([{ id: job_id, label: `#${job_id}` }]);
        setPhase('done');
        progress.track(job_id, action.kind, { onClose: () => setBusy(false) });
        setBusy(false);
      } catch (e) {
        fail(e?.response?.data?.detail || e.message || 'Failed to enqueue');
      }
    },
    [action.kind, progress, fail],
  );

  const onRun = useCallback(() => {
    setError(null);
    setJobLinks([]);
    setPhaseNote(null);
    setCancelling(false);
    // Confirm only for actions that write to BigCommerce. Pulls (Epicor ->
    // staging) never mutate BC and don't need a guard.
    const writesToBC =
      action.kind === 'product' || action.kind === 'customer' || action.kind === 'address_push';
    if (!dryRun && writesToBC) {
      const verb =
        action.id === 'product_changes'
          ? 'apply changes to'
          : action.kind === 'customer'
            ? 'create/update customers in'
            : action.kind === 'address_push'
              ? 'create/update addresses in'
              : 'mutate';
      if (!window.confirm(`This will ${verb} BigCommerce ${target.label} (real write, not dry run). Continue?`)) {
        return;
      }
    }
    if (action.id === 'product_full') runProductFull();
    else if (action.id === 'product_changes') runProductChanges();
    else if (action.id === 'epicor_product_full') runEpicor({ companies: ['AU', 'NZ'], stage: 'products', sync_mode: 'full', to_pim: true, dry_run: dryRun });
    else if (action.id === 'epicor_product_changes') runEpicor({ companies: ['AU', 'NZ'], stage: 'products', sync_mode: 'incremental', to_pim: true, dry_run: dryRun });
    else if (action.id === 'epicor_inventory_full') runEpicor({ companies: ['AU', 'NZ'], stage: 'inventory', sync_mode: 'full', dry_run: dryRun });
    else if (action.id === 'epicor_inventory_changes') runEpicor({ companies: ['AU', 'NZ'], stage: 'inventory', sync_mode: 'incremental', dry_run: dryRun });
    else if (action.id === 'epicor_warehouse_sync') runEpicor({ companies: ['AU', 'NZ'], stage: 'warehouses', dry_run: dryRun });
    else if (action.id === 'epicor_credit_snapshot') runEpicor({ companies: ['AU', 'NZ'], stage: 'credit', dry_run: dryRun });
    else if (action.id === 'customer_full') runEpicor({ companies: [market], stage: 'push_customers', environment, sync_mode: 'full', dry_run: dryRun });
    else if (action.id === 'customer_changes') runEpicor({ companies: [market], stage: 'push_customers', environment, sync_mode: 'incremental', dry_run: dryRun });
    else if (action.id === 'shipto_pull') runEpicor({ companies: [market], stage: 'shipto', sync_mode: 'full', dry_run: dryRun });
    else if (action.id === 'address_push') runEpicor({ companies: [market], stage: 'push_addresses', environment, sync_mode: 'full', dry_run: dryRun });
  }, [action, dryRun, target.label, market, environment, runProductFull, runProductChanges, runEpicor]);

  return (
    <Card title={action.title} desc={action.desc} className="manual-sync-card">
      {isCustomer && (
        <p className="banner info manual-sync-note">
          Pushes Epicor company <strong>{market}</strong> customers to BigCommerce{' '}
          <strong>{target.label}</strong> as B2B Companies: matches existing companies, then
          creates/updates open ones and backfills the BC company id. Closed customers are
          deactivated if already synced, or skipped if never synced. Addresses are handled by the
          Ship-to syncs.
        </p>
      )}

      {phaseNote && <p className="muted manual-sync-phase">{phaseNote}</p>}

      <JobProgressBar kind={action.kind} progress={progress} phase={phase} />
      <JobLinks kind={action.kind} progress={progress} jobLinks={jobLinks} />
      {action.kind === 'product' && <NoOpPushBanner progress={progress} jobLinks={jobLinks} />}
      {cancelling && !isTerminal(progress.status) && (
        <p className="muted manual-sync-phase">Cancelling… the job will stop at its next checkpoint.</p>
      )}

      {error && <div className="banner error">{error}</div>}

      <div className="manual-sync-actions">
        <Button onClick={onRun} disabled={running}>
          {running ? 'Running…' : action.title}
        </Button>
        {progress.id && !isTerminal(progress.status) && (
          <Button variant="secondary" onClick={onCancel} disabled={cancelling}>
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </Button>
        )}
      </div>
    </Card>
  );
};

// Single-entity card: enter one EpicorID (Epicor CustID, e.g. 20DIG01),
// case-insensitive, then pull from Epicor or push to BC.
const SingleEntityCard = ({
  title, desc, target, dryRun, entityLabel, pullStage, pullKind, pushStage, pushKind, pushVerb,
}) => {
  const progress = useJobProgress();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [custId, setCustId] = useState('');
  const [jobLinks, setJobLinks] = useState([]);
  const [activeKind, setActiveKind] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const { market, environment } = target;
  const running = busy || ['pending', 'running'].includes(progress.status);

  // Unique per single card (pull/push stages differ between the two) + target.
  const storageKey = `single:${pushStage}:${target.id}`;

  // Resume on mount: re-attach to the saved job and restore the direction so
  // the right button shows "Running…".
  useEffect(() => {
    const saved = jobStore.get(storageKey);
    if (saved && saved.jobId) {
      setJobLinks([{ id: saved.jobId, label: `#${saved.jobId}` }]);
      setActiveKind(saved.kind);
      progress.track(saved.jobId, saved.kind);
    }
    // Mount-once on the stable storageKey; progress.track/setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Persist while live; clear once terminal.
  useEffect(() => {
    if (!progress.id) return;
    if (isTerminal(progress.status)) {
      jobStore.clear(storageKey);
      setCancelling(false);
    } else {
      jobStore.set(storageKey, { jobId: progress.id, kind: progress.kind });
    }
  }, [progress.id, progress.status, progress.kind, storageKey]);

  const onCancel = useCallback(async () => {
    if (!progress.id) return;
    setCancelling(true);
    try {
      await cancelEpicorJob(progress.id);
    } catch (e) {
      setCancelling(false);
      setError(e?.response?.data?.detail || e.message || 'Failed to cancel');
    }
  }, [progress.id]);

  const run = useCallback(
    async (direction) => {
      setError(null);
      setJobLinks([]);
      setCancelling(false);
      const id = String(custId).trim();
      if (!id) {
        setError('Enter a valid EpicorID (e.g. 20DIG01).');
        return;
      }
      const isPush = direction === 'push';
      if (isPush && !dryRun) {
        if (!window.confirm(`This will ${pushVerb} BigCommerce ${target.label} for EpicorID ${id} (real write, not dry run). Continue?`)) {
          return;
        }
      }
      const kind = isPush ? pushKind : pullKind;
      const payload = isPush
        ? { companies: [market], stage: pushStage, environment, sync_mode: 'full', dry_run: dryRun, cust_ids: [id] }
        : { companies: [market], stage: pullStage, sync_mode: 'full', dry_run: dryRun, cust_ids: [id] };
      setBusy(true);
      setActiveKind(kind);
      try {
        const { job_id } = await createEpicorJob(payload);
        setJobLinks([{ id: job_id, label: `#${job_id}` }]);
        progress.track(job_id, kind, { onClose: () => setBusy(false) });
        setBusy(false);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || 'Failed to enqueue');
        setBusy(false);
      }
    },
    [custId, dryRun, market, environment, pullStage, pullKind, pushStage, pushKind, pushVerb, target.label, progress],
  );

  return (
    <Card title={title} desc={desc} className="manual-sync-card">
      <div className="form-group">
        <label className="form-label" htmlFor={`single-${pullStage}-${pushStage}`}>
          {entityLabel} EpicorID
        </label>
        <input
          id={`single-${pullStage}-${pushStage}`}
          type="text"
          value={custId}
          onChange={(e) => setCustId(e.target.value)}
          placeholder="e.g. 20DIG01"
        />
      </div>

      <JobProgressBar kind={activeKind || pullKind} progress={progress} phase="idle" />
      <JobLinks kind={activeKind || pullKind} progress={progress} jobLinks={jobLinks} />
      {cancelling && !isTerminal(progress.status) && (
        <p className="muted manual-sync-phase">Cancelling… the job will stop at its next checkpoint.</p>
      )}

      {error && <div className="banner error">{error}</div>}

      <div className="manual-sync-actions">
        <Button variant="secondary" onClick={() => run('pull')} disabled={running}>
          {running && activeKind === pullKind ? 'Running…' : 'Pull from Epicor'}
        </Button>
        <Button onClick={() => run('push')} disabled={running}>
          {running && activeKind === pushKind ? 'Running…' : 'Push to BigCommerce'}
        </Button>
        {progress.id && !isTerminal(progress.status) && (
          <Button variant="secondary" onClick={onCancel} disabled={cancelling}>
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </Button>
        )}
      </div>
    </Card>
  );
};

// Job kind per action: the pull runs on the Epicor queue (epicor_product
// counters); compare/mutate run on the sync queue (product counters).
const epicorCardKind = (mode) => (mode === 'pull' ? 'epicor_product' : 'product');

// Single-SKU Epicor -> PIM -> BigCommerce card. "Pull to PIM" pulls one SKU
// straight from Epicor into the PIM under a selected template (Epicor queue,
// AU+NZ, Postgres only). "Compare"/"Push to BigCommerce" then sync that SKU to
// the selected target (market + environment) via the existing sync queue, which
// resolves/stores the BigCommerce id per environment. Enrich/publish the product
// in the PIM between pull and push.
const SingleEpicorProductCard = ({ target, dryRun }) => {
  const progress = useJobProgress();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sku, setSku] = useState('');
  const [familyCode, setFamilyCode] = useState('default');
  const [families, setFamilies] = useState([]);
  const [jobLinks, setJobLinks] = useState([]);
  const [activeMode, setActiveMode] = useState(null); // 'pull' | 'compare' | 'mutate'
  const [cancelling, setCancelling] = useState(false);

  const { market, environment } = target;
  const running = busy || ['pending', 'running'].includes(progress.status);
  const storageKey = `single_epicor_product:${target.id}`;
  const displayKind = epicorCardKind(activeMode || 'pull');

  // Load templates (families) for the dropdown; fall back to "default".
  useEffect(() => {
    let alive = true;
    listFamilies()
      .then((rows) => { if (alive) setFamilies(rows || []); })
      .catch(() => { /* dropdown falls back to default */ });
    return () => { alive = false; };
  }, []);

  // Resume on mount: re-attach to a saved job and restore the action so the
  // right button shows "Running…" and cancel targets the right queue.
  useEffect(() => {
    const saved = jobStore.get(storageKey);
    if (saved && saved.jobId) {
      setJobLinks([{ id: saved.jobId, label: `#${saved.jobId}` }]);
      setActiveMode(saved.mode || null);
      progress.track(saved.jobId, saved.kind || 'epicor_product');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Persist while live; clear once terminal.
  useEffect(() => {
    if (!progress.id) return;
    if (isTerminal(progress.status)) {
      jobStore.clear(storageKey);
      setCancelling(false);
    } else {
      jobStore.set(storageKey, { jobId: progress.id, kind: progress.kind, mode: activeMode });
    }
  }, [progress.id, progress.status, progress.kind, activeMode, storageKey]);

  const onCancel = useCallback(async () => {
    if (!progress.id) return;
    setCancelling(true);
    try {
      // The pull is an Epicor-queue job; compare/mutate are sync-queue jobs.
      if (progress.kind === 'epicor_product') {
        await cancelEpicorJob(progress.id);
      } else {
        await cancelJob(progress.id);
      }
    } catch (e) {
      setCancelling(false);
      setError(e?.response?.data?.detail || e.message || 'Failed to cancel');
    }
  }, [progress.id, progress.kind]);

  const run = useCallback(
    async (mode) => {
      setError(null);
      setJobLinks([]);
      setCancelling(false);
      const id = String(sku).trim();
      if (!id) {
        setError('Enter a SKU.');
        return;
      }
      if (mode === 'mutate' && !dryRun) {
        if (!window.confirm(`This will push SKU ${id} to BigCommerce ${target.label} (real write, not dry run). Continue?`)) {
          return;
        }
      }
      const kind = epicorCardKind(mode);
      setBusy(true);
      setActiveMode(mode);
      try {
        let job_id;
        if (mode === 'pull') {
          // Epicor -> PIM (AU+NZ). dry_run is forced off: a dry pull writes
          // nothing to product_details, so the PIM chain would be a no-op.
          ({ job_id } = await createEpicorJob({
            stage: 'products',
            companies: ['AU', 'NZ'],
            skus: [id],
            family_code: familyCode || undefined,
            to_pim: true,
            dry_run: false,
          }));
        } else if (mode === 'compare') {
          ({ job_id } = await createJob({
            store_code: market, environment, mode: 'compare', skus: [id],
          }));
        } else {
          ({ job_id } = await createJob({
            store_code: market, environment, mode: 'mutate',
            stage_filter: ALL_PRODUCT_STAGES, skus: [id], dry_run: dryRun,
          }));
        }
        setJobLinks([{ id: job_id, label: `#${job_id}` }]);
        progress.track(job_id, kind, { onClose: () => setBusy(false) });
        setBusy(false);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || 'Failed to enqueue');
        setBusy(false);
      }
    },
    [sku, familyCode, dryRun, market, environment, target.label, progress],
  );

  return (
    <Card
      title="Single Product (Epicor → PIM → BigCommerce)"
      desc="Pull one SKU from Epicor into the PIM under a selected template (AU+NZ), enrich/publish it, then push it to BigCommerce. The push targets the market + environment selected above and reads the PIM projection for environments listed in PIM_PROJECTION_ENVIRONMENTS (e.g. staging); only products published in the PIM are pushed."
      className="manual-sync-card"
    >
      <div className="form-group">
        <label className="form-label" htmlFor={`single-epicor-sku-${target.id}`}>SKU</label>
        <input
          id={`single-epicor-sku-${target.id}`}
          type="text"
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          placeholder="e.g. WAT-12345"
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor={`single-epicor-tpl-${target.id}`}>Template</label>
        <select
          id={`single-epicor-tpl-${target.id}`}
          value={familyCode}
          onChange={(e) => setFamilyCode(e.target.value)}
        >
          {families.length === 0 && <option value="default">default</option>}
          {families.map((f) => (
            <option key={f.code} value={f.code}>{f.label} ({f.code})</option>
          ))}
        </select>
      </div>

      <JobProgressBar
        kind={displayKind}
        progress={progress}
        phase={activeMode === 'compare' ? 'comparing' : 'idle'}
      />
      <JobLinks kind={displayKind} progress={progress} jobLinks={jobLinks} />
      {activeMode === 'mutate' && <NoOpPushBanner progress={progress} jobLinks={jobLinks} />}
      {cancelling && !isTerminal(progress.status) && (
        <p className="muted manual-sync-phase">Cancelling… the job will stop at its next checkpoint.</p>
      )}

      {error && <div className="banner error">{error}</div>}

      <div className="manual-sync-actions">
        <Button onClick={() => run('pull')} disabled={running}>
          {running && activeMode === 'pull' ? 'Running…' : 'Pull to PIM'}
        </Button>
        <Button variant="secondary" onClick={() => run('compare')} disabled={running}>
          {running && activeMode === 'compare' ? 'Running…' : 'Compare (BigCommerce)'}
        </Button>
        <Button onClick={() => run('mutate')} disabled={running}>
          {running && activeMode === 'mutate' ? 'Running…' : 'Push to BigCommerce'}
        </Button>
        {progress.id && !isTerminal(progress.status) && (
          <Button variant="secondary" onClick={onCancel} disabled={cancelling}>
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </Button>
        )}
      </div>
    </Card>
  );
};

// Single-SKU product card: enter one SKU, then Compare (read-only diff) or
// Push (mutate) it against the selected BigCommerce target. Product-queue
// analog of SingleEntityCard (which is hard-wired to the Epicor queue).
const SingleProductCard = ({ target, dryRun }) => {
  const progress = useJobProgress();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sku, setSku] = useState('');
  const [jobLinks, setJobLinks] = useState([]);
  const [activeMode, setActiveMode] = useState(null); // 'compare' | 'mutate'
  const [cancelling, setCancelling] = useState(false);

  const { market, environment } = target;
  const running = busy || ['pending', 'running'].includes(progress.status);

  const storageKey = `single_product:${target.id}`;

  // Resume on mount: re-attach to the saved job.
  useEffect(() => {
    const saved = jobStore.get(storageKey);
    if (saved && saved.jobId) {
      setJobLinks([{ id: saved.jobId, label: `#${saved.jobId}` }]);
      setActiveMode(saved.mode || null);
      progress.track(saved.jobId, 'product');
    }
    // Mount-once on the stable storageKey; progress.track/setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Persist while live; clear once terminal.
  useEffect(() => {
    if (!progress.id) return;
    if (isTerminal(progress.status)) {
      jobStore.clear(storageKey);
      setCancelling(false);
    } else {
      jobStore.set(storageKey, { jobId: progress.id, kind: progress.kind, mode: activeMode });
    }
  }, [progress.id, progress.status, progress.kind, activeMode, storageKey]);

  const onCancel = useCallback(async () => {
    if (!progress.id) return;
    setCancelling(true);
    try {
      await cancelJob(progress.id);
    } catch (e) {
      setCancelling(false);
      setError(e?.response?.data?.detail || e.message || 'Failed to cancel');
    }
  }, [progress.id]);

  const run = useCallback(
    async (mode) => {
      setError(null);
      setJobLinks([]);
      setCancelling(false);
      const id = String(sku).trim();
      if (!id) {
        setError('Enter a SKU to sync.');
        return;
      }
      const isPush = mode === 'mutate';
      if (isPush && !dryRun) {
        if (!window.confirm(`This will push SKU ${id} to BigCommerce ${target.label} (real write, not dry run). Continue?`)) {
          return;
        }
      }
      // Compare writes nothing, so it carries no stage_filter and no dry_run.
      const payload = isPush
        ? { store_code: market, environment, mode: 'mutate', stage_filter: ALL_PRODUCT_STAGES, skus: [id], dry_run: dryRun }
        : { store_code: market, environment, mode: 'compare', skus: [id] };
      setBusy(true);
      setActiveMode(mode);
      try {
        const { job_id } = await createJob(payload);
        setJobLinks([{ id: job_id, label: `#${job_id}` }]);
        progress.track(job_id, 'product', { onClose: () => setBusy(false) });
        setBusy(false);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || 'Failed to enqueue');
        setBusy(false);
      }
    },
    [sku, dryRun, market, environment, target.label, progress],
  );

  return (
    <Card
      title="Single Product"
      desc="Sync one SKU for testing. Compare runs a read-only diff for this SKU (streams counters; see the diff detail screen for the per-field delta), or push it to BigCommerce."
      className="manual-sync-card"
    >
      <div className="form-group">
        <label className="form-label" htmlFor={`single-product-${target.id}`}>
          SKU
        </label>
        <input
          id={`single-product-${target.id}`}
          type="text"
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          placeholder="e.g. WAT-12345"
        />
      </div>

      <JobProgressBar
        kind="product"
        progress={progress}
        phase={activeMode === 'compare' ? 'comparing' : 'idle'}
      />
      <JobLinks kind="product" progress={progress} jobLinks={jobLinks} />
      {activeMode === 'mutate' && <NoOpPushBanner progress={progress} jobLinks={jobLinks} />}
      {cancelling && !isTerminal(progress.status) && (
        <p className="muted manual-sync-phase">Cancelling… the job will stop at its next checkpoint.</p>
      )}

      {error && <div className="banner error">{error}</div>}

      <div className="manual-sync-actions">
        <Button variant="secondary" onClick={() => run('compare')} disabled={running}>
          {running && activeMode === 'compare' ? 'Running…' : 'Compare'}
        </Button>
        <Button onClick={() => run('mutate')} disabled={running}>
          {running && activeMode === 'mutate' ? 'Running…' : 'Push to BigCommerce'}
        </Button>
        {progress.id && !isTerminal(progress.status) && (
          <Button variant="secondary" onClick={onCancel} disabled={cancelling}>
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </Button>
        )}
      </div>
    </Card>
  );
};

// Single-SKU inventory card: enter one SKU and pull its stock rows (all
// warehouses, AU + NZ) straight from Epicor. Epicor-queue only — there is no
// BigCommerce push for inventory. A targeted pull never advances the shared
// changes-sync watermark and never marks other rows absent.
const SingleInventorySkuCard = ({ dryRun }) => {
  const progress = useJobProgress();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sku, setSku] = useState('');
  const [jobLinks, setJobLinks] = useState([]);
  const [cancelling, setCancelling] = useState(false);

  const running = busy || ['pending', 'running'].includes(progress.status);
  const storageKey = 'single_inventory_sku';

  // Resume on mount: re-attach to the saved job.
  useEffect(() => {
    const saved = jobStore.get(storageKey);
    if (saved && saved.jobId) {
      setJobLinks([{ id: saved.jobId, label: `#${saved.jobId}` }]);
      progress.track(saved.jobId, 'epicor_inventory');
    }
    // Mount-once on the stable storageKey; progress.track/setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Persist while live; clear once terminal.
  useEffect(() => {
    if (!progress.id) return;
    if (isTerminal(progress.status)) {
      jobStore.clear(storageKey);
      setCancelling(false);
    } else {
      jobStore.set(storageKey, { jobId: progress.id, kind: 'epicor_inventory' });
    }
  }, [progress.id, progress.status, storageKey]);

  const onCancel = useCallback(async () => {
    if (!progress.id) return;
    setCancelling(true);
    try {
      await cancelEpicorJob(progress.id);
    } catch (e) {
      setCancelling(false);
      setError(e?.response?.data?.detail || e.message || 'Failed to cancel');
    }
  }, [progress.id]);

  const run = useCallback(async () => {
    setError(null);
    setJobLinks([]);
    setCancelling(false);
    const id = String(sku).trim();
    if (!id) {
      setError('Enter a SKU.');
      return;
    }
    setBusy(true);
    try {
      const { job_id } = await createEpicorJob({
        companies: ['AU', 'NZ'],
        stage: 'inventory',
        skus: [id],
        dry_run: dryRun,
      });
      setJobLinks([{ id: job_id, label: `#${job_id}` }]);
      progress.track(job_id, 'epicor_inventory', { onClose: () => setBusy(false) });
      setBusy(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to enqueue');
      setBusy(false);
    }
  }, [sku, dryRun, progress]);

  return (
    <Card
      title="Single SKU"
      desc="Refresh stock for one SKU across every warehouse (AU + NZ) straight from Epicor. Useful for spot checks — the targeted pull never shifts the changes-sync watermark. No BigCommerce push: inventory is not synced to BigCommerce."
      className="manual-sync-card"
    >
      <div className="form-group">
        <label className="form-label" htmlFor="single-inventory-sku">SKU</label>
        <input
          id="single-inventory-sku"
          type="text"
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          placeholder="e.g. 019400"
        />
      </div>

      <JobProgressBar kind="epicor_inventory" progress={progress} phase="idle" />
      <JobLinks kind="epicor_inventory" progress={progress} jobLinks={jobLinks} />
      {cancelling && !isTerminal(progress.status) && (
        <p className="muted manual-sync-phase">Cancelling… the job will stop at its next checkpoint.</p>
      )}

      {error && <div className="banner error">{error}</div>}

      <div className="manual-sync-actions">
        <Button variant="secondary" onClick={run} disabled={running}>
          {running ? 'Running…' : 'Pull from Epicor'}
        </Button>
        {progress.id && !isTerminal(progress.status) && (
          <Button variant="secondary" onClick={onCancel} disabled={cancelling}>
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </Button>
        )}
      </div>
      {jobLinks.length > 0 && sku.trim() && isTerminal(progress.status) && progress.status === 'succeeded' && (
        <p className="manual-sync-links">
          <Link to={`/inventory/stock?search=${encodeURIComponent(sku.trim())}`}>
            View stock levels for {sku.trim()}
          </Link>
        </p>
      )}
    </Card>
  );
};

// ── Pricing (customer price lists → BigCommerce) ──
//
// Both cards enqueue BC sync-queue jobs with stage_filter ['price_lists'] and
// payload.pricing_scope 'customer' (strict 1:1: one BC price list + customer
// group per customer). NOTE the queue rejects stage_filter in compare mode,
// so "Compare" is a mutate job with dry_run forced true — the stage routes it
// into the GET-only per-customer compare internally.

// Shared audit-gate hook: consumes the SERVER-computed gate verbatim
// (era-aware match rate, threshold and min-sample floor all live in
// audit_gate_status on the backend) so the banner can never disagree with
// the actual push block.
const useAuditGate = (company) => {
  const [gate, setGate] = useState(null); // server gate payload | null
  useEffect(() => {
    let alive = true;
    getAuditSummary(company)
      .then((summary) => {
        if (!alive) return;
        setGate(summary?.gates?.[company] || null);
      })
      .catch(() => { if (alive) setGate(null); });
    return () => { alive = false; };
  }, [company]);
  return gate;
};

const AuditGateBanner = ({ gate, company }) => {
  if (gate == null) return null;
  const rate = gate.match_rate;
  const threshold = gate.threshold != null ? gate.threshold * 100 : 98;
  const era = gate.era_start ? ` since ${gate.era_start}` : '';
  const belowFloor = gate.min_lines != null && num(gate.clean_lines) < num(gate.min_lines);
  if (gate.passes) {
    return (
      <p className="banner info manual-sync-note">
        Audit gate <strong>passing</strong> for {company}: {(rate * 100).toFixed(1)}% of{' '}
        {fmt(gate.clean_lines)} clean order lines{era} match ({'\u2265'}{threshold}% required).
      </p>
    );
  }
  return (
    <div className="banner warning" role="alert">
      Audit gate <strong>failing</strong> for {company}:{' '}
      {num(gate.clean_lines) === 0
        ? `no audited order lines${era ? ` ${era.trim()}` : ''} yet`
        : `only ${(rate * 100).toFixed(1)}% of ${fmt(gate.clean_lines)} clean order lines${era} match`}
      {' '}({'\u2265'}{threshold}% required).
      {belowFloor && (
        <> The audited sample is also below the minimum of {fmt(gate.min_lines)} lines needed for a gate decision.</>
      )}
      {' '}Real pushes are blocked by the server; dry runs still work. Run the pricing pipeline + audit from the{' '}
      <Link to="/pricing">Pricing dashboard</Link> first.
    </div>
  );
};

// Full per-customer price list sync: every BC-mapped customer with a resolved
// pricing profile gets their own BC price list + customer group.
const PriceListFullSyncCard = ({ target, dryRun }) => {
  const progress = useJobProgress();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [jobLinks, setJobLinks] = useState([]);
  const [cancelling, setCancelling] = useState(false);

  const { market, environment } = target;
  const running = busy || ['pending', 'running'].includes(progress.status);
  const gate = useAuditGate(market);
  const storageKey = `pricing_full:${target.id}`;

  useEffect(() => {
    const saved = jobStore.get(storageKey);
    if (saved && saved.jobId) {
      setJobLinks([{ id: saved.jobId, label: `#${saved.jobId}` }]);
      progress.track(saved.jobId, 'pricing');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    if (!progress.id) return;
    if (isTerminal(progress.status)) {
      jobStore.clear(storageKey);
      setCancelling(false);
    } else {
      jobStore.set(storageKey, { jobId: progress.id, kind: 'pricing' });
    }
  }, [progress.id, progress.status, storageKey]);

  const onCancel = useCallback(async () => {
    if (!progress.id) return;
    setCancelling(true);
    try {
      await cancelJob(progress.id);
    } catch (e) {
      setCancelling(false);
      setError(e?.response?.data?.detail || e.message || 'Failed to cancel');
    }
  }, [progress.id]);

  const onRun = useCallback(async () => {
    setError(null);
    setJobLinks([]);
    setCancelling(false);
    if (!dryRun) {
      if (!window.confirm(
        `This will create/update a price list and customer group in BigCommerce ${target.label} `
        + 'for EVERY mapped customer (real write, not dry run). Continue?',
      )) return;
    }
    setBusy(true);
    try {
      const { job_id } = await createJob({
        store_code: market,
        environment,
        mode: 'mutate',
        stage_filter: ['price_lists'],
        dry_run: dryRun,
        payload: { pricing_scope: 'customer' },
      });
      setJobLinks([{ id: job_id, label: `#${job_id}` }]);
      progress.track(job_id, 'pricing', { onClose: () => setBusy(false) });
      setBusy(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to enqueue');
      setBusy(false);
    }
  }, [market, environment, dryRun, target.label, progress]);

  return (
    <Card
      title="Price List Full Sync"
      desc="Push every eligible customer's resolved price list to BigCommerce, strict 1:1: one BC price list + one customer group per customer, with the customer assigned to their group. Eligible = mapped to a BC customer, assigned a resolved pricing profile, AND active in Epicor (inactive accounts are skipped and counted)."
      className="manual-sync-card"
    >
      <AuditGateBanner gate={gate} company={market} />

      <JobProgressBar kind="pricing" progress={progress} phase="idle" />
      <JobLinks kind="pricing" progress={progress} jobLinks={jobLinks} />
      {cancelling && !isTerminal(progress.status) && (
        <p className="muted manual-sync-phase">Cancelling… the job will stop at its next checkpoint.</p>
      )}

      {error && <div className="banner error">{error}</div>}

      <div className="manual-sync-actions">
        <Button onClick={onRun} disabled={running || (gate != null && !gate.passes && !dryRun)}>
          {running ? 'Running…' : 'Price List Full Sync'}
        </Button>
        {progress.id && !isTerminal(progress.status) && (
          <Button variant="secondary" onClick={onCancel} disabled={cancelling}>
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </Button>
        )}
      </div>
      {gate != null && !gate.passes && !dryRun && (
        <p className="muted manual-sync-phase">
          Blocked until the audit gate passes — tick Dry run above to preview the diff instead.
        </p>
      )}
    </Card>
  );
};

// Single-customer price list card: pick a customer (typeahead), then Compare
// (read-only per-customer diff) or Push their price list to BC.
const SinglePriceListCard = ({ target, dryRun }) => {
  const progress = useJobProgress();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [jobLinks, setJobLinks] = useState([]);
  const [activeMode, setActiveMode] = useState(null); // 'compare' | 'mutate'
  const [cancelling, setCancelling] = useState(false);

  const { market, environment } = target;
  const running = busy || ['pending', 'running'].includes(progress.status);
  const gate = useAuditGate(market);
  const storageKey = `pricing_single:${target.id}`;

  const searchCustomers = useCallback(
    (q) => searchPricingCustomers(market, q).then((d) => d.customers || []),
    [market],
  );

  useEffect(() => {
    const saved = jobStore.get(storageKey);
    if (saved && saved.jobId) {
      setJobLinks([{ id: saved.jobId, label: `#${saved.jobId}` }]);
      setActiveMode(saved.mode || null);
      progress.track(saved.jobId, 'pricing');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    if (!progress.id) return;
    if (isTerminal(progress.status)) {
      jobStore.clear(storageKey);
      setCancelling(false);
    } else {
      jobStore.set(storageKey, { jobId: progress.id, kind: 'pricing', mode: activeMode });
    }
  }, [progress.id, progress.status, activeMode, storageKey]);

  const onCancel = useCallback(async () => {
    if (!progress.id) return;
    setCancelling(true);
    try {
      await cancelJob(progress.id);
    } catch (e) {
      setCancelling(false);
      setError(e?.response?.data?.detail || e.message || 'Failed to cancel');
    }
  }, [progress.id]);

  const run = useCallback(
    async (mode) => {
      setError(null);
      setJobLinks([]);
      setCancelling(false);
      if (!customer) {
        setError('Pick a customer from the dropdown first.');
        return;
      }
      const isPush = mode === 'mutate';
      if (isPush && !dryRun) {
        if (!window.confirm(
          `This will create/update the price list for ${customer.name} in BigCommerce ${target.label} `
          + '(real write, not dry run). Continue?',
        )) return;
      }
      setBusy(true);
      setActiveMode(mode);
      try {
        // Compare MUST be a mutate job with dry_run true: the queue rejects
        // stage_filter in compare mode, and a plain compare job would run the
        // full product compare instead of the price list compare.
        const { job_id } = await createJob({
          store_code: market,
          environment,
          mode: 'mutate',
          stage_filter: ['price_lists'],
          dry_run: isPush ? dryRun : true,
          payload: { pricing_scope: 'customer', cust_nums: [customer.cust_num] },
        });
        setJobLinks([{ id: job_id, label: `#${job_id}` }]);
        progress.track(job_id, 'pricing', { onClose: () => setBusy(false) });
        setBusy(false);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || 'Failed to enqueue');
        setBusy(false);
      }
    },
    [customer, dryRun, market, environment, target.label, progress],
  );

  return (
    <Card
      title="Single Customer Price List"
      desc="Compare or push one customer's price list. Compare is a read-only diff (what would be added / changed / removed in their BC price list); Push creates/updates their BC price list + customer group. Only active Epicor customers are pushed — inactive accounts are skipped and reported."
      className="manual-sync-card"
    >
      <div className="form-group" style={{ minWidth: 320 }}>
        <label className="form-label">Customer (Epicor ID or name)</label>
        <TypeAhead
          placeholder="e.g. ABC100 or ABC Pool"
          search={searchCustomers}
          display={(c) => `${c.name} (${c.cust_id})`}
          pickedLabel={customer ? `${customer.name} (${customer.cust_id})` : ''}
          onPick={setCustomer}
          renderOption={(c) => (
            <span>
              <strong>{c.name}</strong>{' '}
              <span className="muted">
                {c.cust_id} · #{c.cust_num} · {c.group_code || 'no group'}
                {c.has_profile ? '' : ' · ⚠ no pricing profile'}
              </span>
            </span>
          )}
        />
      </div>
      {customer && !customer.has_profile && (
        <div className="banner warning" role="alert">
          This customer has no resolved pricing profile — run the resolve stage first
          (nothing would be pushed).
        </div>
      )}

      <JobProgressBar kind="pricing" progress={progress} phase="idle" />
      <JobLinks kind="pricing" progress={progress} jobLinks={jobLinks} />
      {cancelling && !isTerminal(progress.status) && (
        <p className="muted manual-sync-phase">Cancelling… the job will stop at its next checkpoint.</p>
      )}

      {error && <div className="banner error">{error}</div>}

      <div className="manual-sync-actions">
        <Button variant="secondary" onClick={() => run('compare')} disabled={running}>
          {running && activeMode === 'compare' ? 'Running…' : 'Compare'}
        </Button>
        <Button
          onClick={() => run('mutate')}
          disabled={running || (gate != null && !gate.passes && !dryRun)}
        >
          {running && activeMode === 'mutate' ? 'Running…' : 'Push to BigCommerce'}
        </Button>
        {progress.id && !isTerminal(progress.status) && (
          <Button variant="secondary" onClick={onCancel} disabled={cancelling}>
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </Button>
        )}
      </div>
      {customer && (
        <p className="manual-sync-links">
          <Link to={`/pricing/price-lists?company=${market}&cust_num=${customer.cust_num}`}>
            View price list
          </Link>
        </p>
      )}
    </Card>
  );
};

const TABS = [
  { id: 'products', label: 'Products' },
  { id: 'customers', label: 'Customers' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'warehouses', label: 'Warehouses' },
];

// localStorage keys for the page's persisted UI selections.
const UI_KEYS = { targetId: 'ui.targetId', activeTab: 'ui.activeTab', dryRun: 'ui.dryRun' };

const ManualSyncs = () => {
  // Lazy-init from storage, validating against the current options so a stale
  // or removed value (e.g. a target id that no longer exists) falls back to the
  // default rather than rendering a broken selection.
  const [targetId, setTargetId] = useState(() => {
    const saved = uiStore.get(UI_KEYS.targetId, DEFAULT_SYNC_TARGET);
    return SYNC_TARGETS.some((t) => t.id === saved) ? saved : DEFAULT_SYNC_TARGET;
  });
  const [dryRun, setDryRun] = useState(() => uiStore.get(UI_KEYS.dryRun, false) === true);
  const [activeTab, setActiveTab] = useState(() => {
    const saved = uiStore.get(UI_KEYS.activeTab, 'products');
    return TABS.some((t) => t.id === saved) ? saved : 'products';
  });

  // Persist each selection whenever it changes.
  useEffect(() => {
    uiStore.set(UI_KEYS.targetId, targetId);
  }, [targetId]);
  useEffect(() => {
    uiStore.set(UI_KEYS.activeTab, activeTab);
  }, [activeTab]);
  useEffect(() => {
    uiStore.set(UI_KEYS.dryRun, dryRun);
  }, [dryRun]);

  const target = useMemo(
    () => SYNC_TARGETS.find((t) => t.id === targetId) || SYNC_TARGETS[0],
    [targetId],
  );

  return (
    <div className="sync-page manual-syncs">
      <PageHeader
        title="Manual Syncs"
        sub="Pick a target, choose an action, and watch it run. Products push to the selected BigCommerce store; customer and ship-to syncs pull from Epicor into staging and push to BigCommerce."
      />

      <Card className="manual-sync-controls">
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="manual-sync-target">Target</label>
            <select
              id="manual-sync-target"
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
            >
              {SYNC_TARGETS.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
              {' '}Dry run (no BigCommerce mutations / no Epicor writes; counters only)
            </label>
          </div>
        </div>

        <WorkerStatusBanner marketCode={target.market} environment={target.environment} />
      </Card>

      <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} label="Sync sections" />

      <TabPanel id="products" active={activeTab}>
        <h3 className="section-subtitle">Akeneo</h3>
        <p className="muted manual-sync-phase">
          Push Akeneo-sourced products to BigCommerce {target.label}. Transitional &mdash; retired once
          Epicor fully owns the product source.
        </p>
        <div className="manual-sync-grid">
          {PRODUCT_ACTIONS.map((action) => (
            <ActionCard key={`${action.id}:${target.id}`} action={action} target={target} dryRun={dryRun} />
          ))}
          <SingleProductCard key={`single_product:${target.id}`} target={target} dryRun={dryRun} />
        </div>

        <h3 className="section-subtitle">Epicor</h3>
        <p className="muted manual-sync-phase">
          Pull product master data directly from Epicor (AU + NZ) into the PIM. The bulk pulls below
          write core ERP fields only and ignore the target selector above. The single-product card
          can also push a SKU to BigCommerce, which DOES use the selected market + environment.
        </p>
        <div className="manual-sync-grid">
          {EPICOR_PRODUCT_ACTIONS.map((action) => (
            <ActionCard key={`${action.id}:${target.id}`} action={action} target={target} dryRun={dryRun} />
          ))}
          <SingleEpicorProductCard key={`single_epicor_product:${target.id}`} target={target} dryRun={dryRun} />
        </div>
      </TabPanel>

      <TabPanel id="customers" active={activeTab}>
        <h3 className="section-subtitle">Customer syncs</h3>
        <div className="manual-sync-grid">
          {CUSTOMER_PUSH_ACTIONS.map((action) => (
            <ActionCard key={`${action.id}:${target.id}`} action={action} target={target} dryRun={dryRun} />
          ))}
          <SingleEntityCard
            key={`single_customer:${target.id}`}
            title="Single Customer"
            desc="Sync/create one customer. Pull just this customer (and its ship-tos) from Epicor into staging, or push it to BigCommerce as a B2B Company."
            target={target}
            dryRun={dryRun}
            entityLabel="Customer"
            pullStage="customers"
            pullKind="customer_pull"
            pushStage="push_customers"
            pushKind="customer"
            pushVerb="create/update the company in"
          />
        </div>

        <h3 className="section-subtitle">Ship-to syncs</h3>
        <div className="manual-sync-grid">
          {SHIPTO_ACTIONS.map((action) => (
            <ActionCard key={`${action.id}:${target.id}`} action={action} target={target} dryRun={dryRun} />
          ))}
          <SingleEntityCard
            key={`single_address:${target.id}`}
            title="Single Address"
            desc="Sync/create one customer's addresses. Pull just this customer's ship-tos from Epicor into staging, or push its addresses to BigCommerce."
            target={target}
            dryRun={dryRun}
            entityLabel="Customer"
            pullStage="shipto"
            pullKind="shipto_pull"
            pushStage="push_addresses"
            pushKind="address_push"
            pushVerb="create/update addresses in"
          />
        </div>

        <h3 className="section-subtitle">Credit availability</h3>
        <p className="muted manual-sync-phase">
          Recompute how much credit each customer has left, which gates buy-on-account in the
          B2B storefront. This normally runs automatically after every Customer sync, so the
          card is only needed to rebuild the snapshot out of band. It covers the same customers
          the company push considers eligible, ignores the target selector above, and never
          touches BigCommerce. To refresh a single customer, use the Refresh credit button on
          their customer page.
        </p>
        <div className="manual-sync-grid">
          {EPICOR_CREDIT_ACTIONS.map((action) => (
            <ActionCard key={action.id} action={action} target={target} dryRun={dryRun} />
          ))}
        </div>
      </TabPanel>

      <TabPanel id="pricing" active={activeTab}>
        <h3 className="section-subtitle">Customer price lists</h3>
        <p className="muted manual-sync-phase">
          Push resolved Epicor customer pricing to BigCommerce {target.label} as per-customer
          price lists (strict 1:1: one BC price list + customer group per customer). Real pushes
          are gated on the pricing audit; browse the data first in the{' '}
          <Link to="/pricing/price-lists">Price Lists viewer</Link>.
        </p>
        <div className="manual-sync-grid">
          <PriceListFullSyncCard key={`pricing_full:${target.id}`} target={target} dryRun={dryRun} />
          <SinglePriceListCard key={`pricing_single:${target.id}`} target={target} dryRun={dryRun} />
        </div>
      </TabPanel>

      <TabPanel id="inventory" active={activeTab}>
        <h3 className="section-subtitle">Inventory syncs</h3>
        <p className="muted manual-sync-phase">
          Pull per-SKU, per-warehouse stock from Epicor (AU + NZ) into the middleware. These jobs
          ignore the target selector above and never touch BigCommerce — the B2B site consumes
          stock via the middleware availability API. Browse the data in{' '}
          <Link to="/inventory/stock">Stock Levels</Link>.
        </p>
        <div className="manual-sync-grid">
          {EPICOR_INVENTORY_ACTIONS.map((action) => (
            <ActionCard key={action.id} action={action} target={target} dryRun={dryRun} />
          ))}
          <SingleInventorySkuCard key="single_inventory_sku" dryRun={dryRun} />
        </div>
      </TabPanel>

      <TabPanel id="warehouses" active={activeTab}>
        <h3 className="section-subtitle">Warehouse master</h3>
        <p className="muted manual-sync-phase">
          Refresh the warehouse list from Epicor (AU + NZ). This also happens automatically at
          the start of every inventory sync, so this card is only needed to pick up a new or
          renamed warehouse right away. Manage which warehouses are in use on the{' '}
          <Link to="/configuration?tab=warehouses">Configuration &rsaquo; Warehouses</Link> page.
        </p>
        <div className="manual-sync-grid">
          {EPICOR_WAREHOUSE_ACTIONS.map((action) => (
            <ActionCard key={action.id} action={action} target={target} dryRun={dryRun} />
          ))}
        </div>
      </TabPanel>
    </div>
  );
};

export default ManualSyncs;
